import { call, put, SagaGenerator, select } from "typed-redux-saga";

import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LocalizableArgs } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { StoreSagas } from "@com.mgmtp.a12.client/client-core/lib/core/store";

import { DOC_LOCK_OPERATION, OperationLockState } from "../app/slices/operation/types";
import { isDocumentWithProcessMetadata } from "../modules/workflows/data/processMetadata";
import { lockProcessError } from "../modules/processFormEngine/store/actions";
import { setOperationLoading } from "../app/slices/operation/reducers";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../localization";
import { createCustomNotificationAction } from "../app/actions";
import { selectOperationLockState } from "../app/slices/operation/selectors";
import { TASK_DETAIL_ACTIVITY_ID } from "../modules/processOverview/store/actions";

import { getUserInfo, isUser, selectStructuredRoles, UserInfo } from "./user";
import { parseDateTimeForA12 } from "./dateTimeUtils";
import { lockDocumentOperation, LockDocumentOperationResponse } from "./rpcHelper";
import { selectDocument } from "./storeHelpers";

export const DOCUMENT_LOCK_GROUP = "DOCUMENT";

export function* hasBetrachterRoleForDocument(document: unknown): SagaGenerator<boolean> {
	const user = yield* select(UaaSelectors.user);

	if (!isUser(user) || !isDocumentWithProcessMetadata(document)) {
		return false;
	}

	const fqn = document.root.processMetadata.fachverfahren.fqn;
	if (!fqn) {
		return false;
	}

	return selectStructuredRoles(user)
		.filter(role => role.roles && role.roles.length === 1)
		.some(role => {
			const roleFqn =
				role.client && typeof role.client === "string" && role.client.length > 0
					? [role.key, role.client].filter(Boolean).join("_")
					: role.key;

			return (
				roleFqn.toUpperCase() === fqn.toUpperCase() &&
				(role.roles.includes("betrachter") || role.roles.includes("BETRACHTER"))
			);
		});
}

export function isProcessLocked(lockState: OperationLockState | undefined, user: unknown): boolean {
	if (!lockState) {
		return false;
	}
	return isUser(user) && lockState.userId !== user.profile.preferred_username;
}

export function shouldUnLockProcess(lockState: OperationLockState | undefined, user: unknown): boolean {
	return !isProcessLocked(lockState, user);
}

export function shouldRenewLockProcess(lockState: OperationLockState | undefined, user: unknown): boolean {
	return !isProcessLocked(lockState, user);
}

export function getLockProcessError(lockState: OperationLockState | undefined): string {
	if (lockState && lockState.userFullName && lockState.lockedAt) {
		const lockedAt = formatLockedAtForMessage(lockState.lockedAt);
		return `Benutzer:in ${lockState.userFullName}
	bearbeitet diesen Vorgang ${lockedAt ?? ""}.`;
	}
	return "";
}

function formatLockedAtForMessage(lockedAt: string | Date | undefined): string {
	if (!lockedAt) {
		return "";
	}

	const date = typeof lockedAt === "string" ? parseDateTimeForA12(lockedAt) : lockedAt;
	if (date === null) {
		return "";
	}

	const pad = (value: number): string => value.toString().padStart(2, "0");
	return `seit dem ${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}
	um ${pad(date.getHours())}:${pad(date.getMinutes())} Uhr`;
}

export function* validateLockProcess(docRef: string, activityId: string): SagaGenerator<boolean> {
	const user = yield* select(UaaSelectors.user);
	if (isUser(user) && user.profile.preferred_username) {
		const userInfo = getUserInfo(user);

		if (!userInfo) {
			return true;
		}
		const response = yield* callLockDocumentOperation(docRef, activityId, "IS_LOCKED", userInfo);
		if (response && response.errorCode === "LOCK_ERROR") {
			yield* put(lockProcessError({}));
			return false;
		}
	}
	return true;
}

export function* validateLockState(): SagaGenerator<boolean> {
	const user = yield* select(UaaSelectors.user);
	if (isUser(user) && user.profile.preferred_username) {
		const userInfo = getUserInfo(user);

		const document = yield* call(() => StoreSagas.waitForStateChange(selectDocument(TASK_DETAIL_ACTIVITY_ID)));

		if (!isDocumentWithProcessMetadata(document) || !userInfo) {
			return true;
		}

		const lockState = yield* select(selectOperationLockState(document.id));

		return !isProcessLocked(lockState, user);
	}
	return true;
}

export function* callLockDocumentOperation(
	docRef: string,
	activityId: string,
	method: string,
	userInfo: UserInfo
): SagaGenerator<LockDocumentOperationResponse | undefined> {
	let response: LockDocumentOperationResponse | undefined = undefined;
	yield* put(setOperationLoading({ operationName: DOC_LOCK_OPERATION, loadingState: "loading" }));
	try {
		response = yield* call(lockDocumentOperation, docRef, method, userInfo.userId, userInfo.userFullName);
	} catch (error) {
		console.error("error while calling lockDocumentOperation!", error);
		yield* showSavingErrorNotification(activityId);
		return undefined;
	} finally {
		yield* put(setOperationLoading({ operationName: DOC_LOCK_OPERATION, loadingState: "loaded" }));
	}
	return response;
}

export function* showSavingErrorNotification(activityId: string): SagaGenerator<void> {
	yield* showErrorNotification(
		activityId,
		RESOURCE_KEYS.wf.notification.error.savingFailedTitle,
		RESOURCE_KEYS.wf.notification.error.savingFailedMessage
	);
}

function* showErrorNotification(
	activityId: string,
	title: string,
	message: string,
	args?: LocalizableArgs
): SagaGenerator<void> {
	yield* put(
		createCustomNotificationAction({
			title: localizableFromLocaleResources(title),
			message: localizableFromLocaleResources(message, args),
			severity: "error",
			activityId,
			group: "lockProcess"
		})
	);
}

export function* showLockErrorNotification(activityId: string): SagaGenerator<void> {
	yield* showErrorNotification(
		activityId,
		RESOURCE_KEYS.wf.notification.error.lockProcessErrorTitle,
		RESOURCE_KEYS.wf.notification.error.lockProcessErrorMessage,
		{}
	);
}
